import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  ImageList,
  ImageListItem,
} from '@mui/material';
import { ArrowBack, Gavel, Notifications, Share } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AuctionCountdown from '../components/AuctionCountdown';
import BidForm from '../components/BidForm';
import BidHistory from '../components/BidHistory';
import WatchButton from '../components/WatchButton';
import api from '../services/api';

const AuctionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchAuction();
    fetchBids();

    // Poll for updates every 10 seconds
    const interval = setInterval(() => {
      fetchAuction();
      fetchBids();
    }, 10000);

    return () => clearInterval(interval);
  }, [id]);

  const fetchAuction = async () => {
    try {
      const response = await api.get(`/auctions/${id}`);
      setAuction(response.data.auction);
      setError('');
    } catch (err) {
      setError('Failed to load auction');
      console.error('Error fetching auction:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async () => {
    try {
      const response = await api.get(`/auctions/${id}/bids`);
      setBids(response.data.bids || []);
    } catch (err) {
      console.error('Error fetching bids:', err);
    }
  };

  const handleBidPlaced = (data) => {
    setAuction(data.auction);
    setBids([data.bid, ...bids]);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !auction) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Alert severity="error">{error || 'Auction not found'}</Alert>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/auctions')} sx={{ mt: 2 }}>
            Back to Auctions
          </Button>
        </Box>
      </Container>
    );
  }

  const images = auction.item?.photos || [];
  const mainImage = images[selectedImage]?.url || '/placeholder-item.jpg';

  return (
    <Container maxWidth="xl">
      <Box py={4}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/auctions')} sx={{ mb: 3 }}>
          Back to Auctions
        </Button>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <CardMedia
                component="img"
                image={mainImage}
                alt={auction.item?.name}
                sx={{
                  height: 500,
                  objectFit: 'contain',
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              />
            </Paper>

            {images.length > 1 && (
              <ImageList cols={4} gap={8}>
                {images.map((photo, index) => (
                  <ImageListItem
                    key={photo.id}
                    onClick={() => setSelectedImage(index)}
                    sx={{
                      cursor: 'pointer',
                      border: selectedImage === index ? 2 : 1,
                      borderColor: selectedImage === index ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <img src={photo.url} alt={`${auction.item?.name} ${index + 1}`} loading="lazy" />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            <Paper sx={{ p: 3, mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Item Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body1" paragraph>
                {auction.item?.description || 'No description available'}
              </Typography>

              {auction.item?.category && (
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2">{auction.item.category}</Typography>
                </Box>
              )}

              {auction.item?.sku && (
                <Box mb={1}>
                  <Typography variant="caption" color="text.secondary">
                    SKU
                  </Typography>
                  <Typography variant="body2">{auction.item.sku}</Typography>
                </Box>
              )}
            </Paper>

            <Paper sx={{ p: 3, mt: 2 }}>
              <BidHistory bids={bids} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, mb: 2, position: 'sticky', top: 20 }}>
              <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                <Typography variant="h5" fontWeight="bold">
                  {auction.item?.name}
                </Typography>
                <Chip label={auction.status} color={auction.status === 'active' ? 'success' : 'default'} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box mb={3}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Current Bid
                </Typography>
                <Typography variant="h3" color="primary.main" fontWeight="bold">
                  {formatPrice(auction.current_bid || auction.start_price)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {auction.bid_count || 0} bid{auction.bid_count !== 1 ? 's' : ''}
                </Typography>
              </Box>

              {auction.status === 'active' && (
                <>
                  <Box mb={3}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Time Remaining
                    </Typography>
                    <AuctionCountdown endTime={auction.end_time} />
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <BidForm auction={auction} onBidPlaced={handleBidPlaced} />

                  <Box mt={2}>
                    <WatchButton auctionId={auction.id} isWatching={false} />
                  </Box>
                </>
              )}

              {auction.status === 'ended' && (
                <Box textAlign="center" py={2}>
                  <Typography variant="h6" color="text.secondary">
                    Auction Ended
                  </Typography>
                  {auction.winner_id && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Winning bid: {formatPrice(auction.winning_bid)}
                    </Typography>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Auction Start
                </Typography>
                <Typography variant="body2" mb={2}>
                  {formatDate(auction.start_time)}
                </Typography>

                <Typography variant="caption" color="text.secondary" display="block">
                  Auction End
                </Typography>
                <Typography variant="body2">
                  {formatDate(auction.end_time)}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AuctionDetail;
